
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { trackError } from '@/utils/error/errorTracker';
import ErrorFallback from '@/components/layouts/ErrorFallback';

interface Props {
  children: ReactNode;
  componentName?: string;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Unified ErrorBoundary component
 * Simplified implementation with better performance and recovery options
 */
class ErrorBoundary extends Component<Props, State> {
  // Track mounted status to prevent updates after unmount
  private isMounted: boolean = false;
  
  // Prevent error thrashing with improved cooldown
  private lastErrorTime: number = 0;
  private errorCount: number = 0;
  private errorLog: Map<string, number> = new Map();
  private errorThreshold: number = 3; // Reduced threshold for production
  private errorCooldownMs: number = 20000; // 20 seconds between error groups
  
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  // Update mounted status
  componentDidMount() {
    this.isMounted = true;
  }
  
  componentWillUnmount() {
    this.isMounted = false;
    this.errorLog.clear(); // Clean up error log
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (!this.isMounted) return;
    
    this.setState({ errorInfo });
    
    const now = Date.now();
    this.errorCount++;
    
    // Track error message frequency
    const errorMessage = error.message || 'Unknown error';
    const errorHash = this.getErrorHash(errorMessage);
    
    // Update error count in log
    this.errorLog.set(errorHash, (this.errorLog.get(errorHash) || 0) + 1);
    
    // Check for error thrashing
    const isErrorThrashing = this.errorCount > this.errorThreshold && 
      (now - this.lastErrorTime < this.errorCooldownMs);
    
    // Check for duplicate errors (same error happening repeatedly)
    const isDuplicateError = (this.errorLog.get(errorHash) || 0) > 2;
    
    this.lastErrorTime = now;
    
    // Get component info for context
    const componentName = this.props.componentName || 'UnknownComponent';
    
    // Only track errors that aren't duplicates or thrashing
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development' || (!isErrorThrashing && !isDuplicateError)) {
      trackError(error, `ErrorBoundary:${componentName}`, {
        componentStack: errorInfo.componentStack?.split('\n').slice(0, 3).join('\n')
      });
    }
    
    // Call custom error handler if provided
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch {
        // Silent fail - never cascade errors
      }
    }

    // Log directly in development
    if (process.env.NODE_ENV !== 'production' && !isErrorThrashing) {
      console.error(`🔴 Error in ${componentName}:`, error.message);
    }
  }

  // Simple hash function for error deduplication
  private getErrorHash(message: string): string {
    // Use only first 50 chars max
    const trimmed = message.slice(0, 50);
    let hash = 0;
    for (let i = 0; i < trimmed.length; i++) {
      hash = ((hash << 5) - hash) + trimmed.charCodeAt(i);
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  resetError = () => {
    if (this.isMounted) {
      // Reset error state
      this.setState({ hasError: false, error: null, errorInfo: null });
      this.errorCount = Math.max(0, this.errorCount - 3);
    }
  };

  render() {
    if (this.state.hasError) {
      // If we're in an error thrashing situation, display a minimal fallback
      if (this.errorCount > this.errorThreshold * 2) {
        return (
          <div className="p-4 bg-gray-50 rounded-md text-gray-800 shadow-sm">
            <h3 className="font-medium">Component Error</h3>
            <p className="text-sm mt-1">This component has encountered multiple errors.</p>
            <button 
              onClick={this.resetError}
              className="mt-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              Reset Component
            </button>
          </div>
        );
      }
      
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default to ErrorFallback component
      return (
        <ErrorFallback 
          error={this.state.error!}
          errorInfo={this.state.errorInfo!}
          resetErrorBoundary={this.resetError}
          componentName={this.props.componentName}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
